import React, { FC, useRef, useState } from 'react';
import userDefaultImage from '../../../../assets/images/user-default.jpg';
import { IDevice } from '../../../../types';
import { baseURL, updateDeviceProfilePicture } from '../../../../services';
import { useGlobal } from '../../../../hooks/useGlobal';

interface IAgentProfileCard {
  deviceInfo: any;
  loading: boolean;
  deviceDetail?: IDevice;
  pageAction?: any;
  onUpdate?: () => void;
}

export const AgentProfileCard: FC<IAgentProfileCard> = ({
  deviceInfo,
  loading,
  deviceDetail,
  pageAction,
  onUpdate,
}) => {
  const { showAlert } = useGlobal();
  const [profilePictureLoading, setProfilePictureLoading] =
    useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { status, picture, name } = deviceInfo?.profile || {};
  const profilePicture = baseURL + (deviceDetail?.profile?.picture || '');
  const displayName = deviceDetail?.profile?.displayName || name;
  const aboutText = deviceDetail?.profile?.aboutText || status;

  const handleProfilePictureUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setProfilePictureLoading(true);

    try {
      await updateDeviceProfilePicture(deviceDetail?._id || '', file);

      // Call onUpdate callback if provided
      if (onUpdate) {
        onUpdate();
      }
    } catch (error: any) {
      console.error('Profile picture upload failed:', error);
      showAlert({
        text: error.message || 'Profil fotoğrafı yüklenirken bir hata oluştu',
        icon: 'error',
      });
    } finally {
      setProfilePictureLoading(false);
      // Reset file input
      e.target.value = '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center space-x-4 animate-pulse">
        {/* Profile Picture Skeleton */}
        <div className="flex-shrink-0">
          <div className="h-20 w-20 rounded-full bg-gray-200 border-2 border-gray-200 shadow-sm dark:bg-gray-700 dark:border-gray-600"></div>
        </div>

        {/* Profile Info Skeleton */}
        <div className="flex-1 min-w-0">
          {/* Profile Name Skeleton */}
          <div className="h-5 bg-gray-200 rounded-full dark:bg-gray-700 w-32 mb-2"></div>

          {/* About/Status Skeleton */}
          <div className="h-4 bg-gray-200 rounded-full dark:bg-gray-700 w-20"></div>
        </div>

        <span className="sr-only">Loading...</span>
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-4">
      {/* Profile Picture */}
      <div className="flex-shrink-0 relative">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleProfilePictureUpload}
          disabled={profilePictureLoading || !pageAction?.edit}
          className="hidden"
        />
        <img
          src={profilePicture || picture?.imgFull || userDefaultImage}
          alt={displayName}
          onClick={() => {
            if (pageAction?.edit && !profilePictureLoading) {
              fileInputRef.current?.click();
            }
          }}
          className={`h-20 w-20 rounded-full object-cover border-2 border-gray-200 shadow-sm ${
            pageAction?.edit && !profilePictureLoading
              ? 'cursor-pointer hover:opacity-80 transition-opacity'
              : ''
          }`}
        />
        {profilePictureLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 rounded-full">
            <span className="text-white text-xs">Yükleniyor...</span>
          </div>
        )}
      </div>

      {/* Profile Info */}
      <div className="flex-1 min-w-0">
        {/* Profile Name */}
        <div className="text-lg font-semibold text-gray-900 mb-1">
          {displayName}
        </div>

        {/* About/Status */}
        <div className="text-base text-gray-500">{aboutText}</div>
      </div>
    </div>
  );
};
